<template>
  <div @focusout="onFocusOut">
    <!-- 予約確定時に体験の予約種別がリクエスト予約へ変わっていたため案内する。即時確定→承認制という変更（承認までカードはオーソリのみ）を主役に伝え、決済方法の切替とカード再入力は従属情報として示す。 -->
    <div v-if="reservationTypeChangedToFincode" class="mb-4 border-l-4 border-gold1 bg-beige1 px-4 py-4 text-sm leading-relaxed text-gray-800 md:px-10" data-testid="reservation-type-changed-banner">
      This experience is now a request-based booking. It will need to be approved by the host before it's confirmed, and your card will be authorized (not charged) until then. We've kept your
      details—please re-enter your card below to continue.
    </div>
    <!-- 新動線で中身が変わるセクションは、新動線専用の部品に分けてここで出し分ける（旧動線の部品には分岐を入れない。D-025） -->
    <ReservationFormSection
      class="mb-4"
      section-id="contactInfo"
      title="Contact Info"
      :accordion="accordion"
      :is-accordion="isAccordion"
      :is-fulfilled="sectionFulfillment.contactInfo"
      :subtitle="contactInfoSubtitle"
      has-continue-button
    >
      <CompactGuestDetailSection v-if="isAccordion" :get-error-message="getSectionErrorMessage" />
      <GuestDetailSection v-else :apply-code="applyCode" :get-error-message="getSectionErrorMessage" :remove-code="removeCode" />
    </ReservationFormSection>
    <!-- 通訳オプション・体験オプション選択セクション -->
    <ReservationFormSection
      class="mb-4"
      section-id="additionalOptions"
      title="Additional Options"
      :accordion="accordion"
      :is-accordion="isAccordion"
      :is-fulfilled="sectionFulfillment.additionalOptions"
      has-continue-button
    >
      <CompactOptionSection v-if="isAccordion" v-bind="optionFieldsProps" />
      <OptionSection
        v-else
        v-bind="optionFieldsProps"
        :pre-reservation-price="preReservationPrice"
        :pre-reservation-price-with-guest-type="preReservationPriceWithGuestType"
        :reservation-step1-form="form.step1"
        :reservation-step2-form="form.step2"
      />
    </ReservationFormSection>
    <!-- 新動線では予約質問が 0 件でもセクションを畳んだまま残す。電話・国籍が常に必須で自動完了しないため（D-029） -->
    <ReservationFormSection
      v-if="isAccordion || hasReservationQuestions"
      class="mb-4"
      section-id="hostDetails"
      title="A few details for the host"
      :accordion="accordion"
      :is-accordion="isAccordion"
      :is-fulfilled="sectionFulfillment.hostDetails"
      has-continue-button
    >
      <HostDetailsSection v-if="isAccordion" :get-error-message="getSectionErrorMessage" />
      <AdditionalInfoSection v-else :get-error-message="getSectionErrorMessage" />
    </ReservationFormSection>
    <ReservationFormSection
      v-if="experienceEvent.id"
      section-id="payment"
      title="Payment Information"
      :accordion="accordion"
      :is-accordion="isAccordion"
      :is-fulfilled="sectionFulfillment.payment"
    >
      <div v-if="!shouldHidePaymentForm">
        <CreditCardFields v-if="isAccordion" :key="paymentFormInstanceKey" class="mt-4" :payment-error-messages="paymentErrorMessages" :payment-method="paymentMethod" />
        <CreditCardSection v-else :key="paymentFormInstanceKey" class="mt-4" :payment-error-messages="paymentErrorMessages" :payment-method="paymentMethod" />
      </div>
      <div v-else-if="shouldSkipPayment" class="mb-4">
        <h3 v-if="!isAccordion" class="mb-5 tracking-wide md:mb-4 md:text-[22px] md:text-xl">Payment Details</h3>
        <span data-testid="skip-payment-message">You can skip entering the payment details.</span>
      </div>
      <!-- キャンセルポリシー表示セクション -->
      <CancelPolicySection
        :cancellation-policy-trigger="experienceEvent.cancellationPolicyTrigger"
        :is-ladder-enabled="experienceEvent.experience.isLadderEnabled"
        :refundable-cancellation-days-for-guest="experienceEvent.experience.refundableCancellationDaysForGuest"
      />
      <!-- 新動線の特典コードは支払いの段で入れる。最初の画面で持っていないものを訊かない（D-028 / D-031） -->
      <ReservationCodeForm v-if="isAccordion && codeRedemption" class="mt-8" :applied-code-error="submittedCodeErrorMessage" :redemption="codeRedemption" />
      <!-- 同意チェックボックス -->
      <div class="mb-4 mt-8 flex items-start gap-x-3">
        <input id="terms-consent" v-model="hasConsented" class="mt-1 h-4 w-4 cursor-pointer accent-gold1" type="checkbox" />
        <label class="cursor-pointer text-sm leading-relaxed" for="terms-consent">
          I have read and agree to the
          <a class="text-gold1 underline" href="https://wabunka-lux.jp/pdf/privacy-policy.pdf" target="_blank">Privacy Policy</a>,
          <a class="text-gold1 underline" href="https://wabunka-lux.jp/pdf/terms.pdf" target="_blank">Terms of Use</a>, and
          <a class="text-gold1 underline" href="https://wabunka-lux.jp/pdf/tour-terms-and-conditions.pdf" target="_blank">Tour Terms & Conditions</a>.
        </label>
      </div>
      <div class="item-center flex flex-col justify-center">
        <template v-if="hasError && isPaymentSectionExpanded">
          <AtomsErrorText v-for="errMsg in errorMessages" :key="errMsg" lang="en">{{ errMsg }}</AtomsErrorText>
        </template>
        <AtomsPrimaryButton class="mb-4 w-full" data-testid="submit" gtm-id="addPaymentCard" :disabled="isSending || isPriceLoading || !hasConsented || codeRedemption?.isRedeeming || codeRedemption?.isAwaitingPrice" :on-click="onClickSubmit">{{
          !isSending ? 'Next' : 'Processing...'
        }}</AtomsPrimaryButton>
        <!-- 新動線は Back を出さない。手前のセクションへは Edit で、体験詳細へはブラウザの戻るで戻れる（D-039） -->
        <AtomsTertiaryButton v-if="!isAccordion" class="w-full" data-testid="back-button" :disabled="false" :on-click="props.onClickBackButton">
          <span>Back</span>
        </AtomsTertiaryButton>
      </div>
      <div class="mx-auto mb-10 max-w-[220px] md:max-w-[328px]"></div>
    </ReservationFormSection>
    <!-- 新動線で S4 が畳まれたときだけ、送信の集約エラーをアコーディオンの外に出す。中に置くと、未充足のセクションを開いた拍子に
         S4 ごと畳まれて消え、ゲストには何が起きたのか分からなくなる -->
    <div v-if="hasError && !isPaymentSectionExpanded" class="bg-white px-4 pb-8 md:px-10">
      <AtomsErrorText v-for="errMsg in errorMessages" :key="errMsg" lang="en">{{ errMsg }}</AtomsErrorText>
    </div>
  </div>
</template>

<script setup lang="ts">
import { getMessageByFieldName } from '@/models/error';
import { sleep } from '@/utils/sleep';
import { useReservationSectionGaEvents } from '@/wabunka-src/common/composables/events/useReservationSectionGaEvents';
import { useReservationAccordion } from '@/wabunka-src/common/features/Reservation/composables/accordion/useReservationAccordion';
import { useGlobalWabunkaReservationForm } from '@/wabunka-src/common/features/Reservation/composables/forms/useGlobalWabunkaReservationForm';
import type { ReservationCodeRedemption } from '@/wabunka-src/common/features/Reservation/composables/forms/useReservationCodeRedemption';
import { isCompanyOrSomeoneElseFormOpen } from '@/wabunka-src/common/features/Reservation/core/requiredFields/companyOrSomeoneElseForm';
import {
  FULFILLABLE_RESERVATION_SECTION_IDS,
  isReservationSectionFulfilled,
  listReservationSectionErrors,
  type FulfillableReservationSectionId,
} from '@/wabunka-src/common/features/Reservation/core/requiredFields/sections';
import { captureException } from '@/wabunka-src/common/utils';
import type { PaymentMethod } from '@/wabunka-src/constants/paymentMethod';
import { OptionSection, CreditCardSection, GuestDetailSection, AdditionalInfoSection, CancelPolicySection } from '@/wabunka-src/features/Experience';
import { usePaymentClient, type ExperiencePreReservationPriceViewModel } from '@/wabunka-src/features/Experience';
import type { WabunkaExperienceEventViewModel } from '@/wabunka-src/features/Experience/models/experienceEvent';
import type { ExperiencePreReservationPriceWithGuestTypeViewModel } from '@/wabunka-src/features/Experience/subFeatures/Reservation/models/wabunkaExperiencePreReservationPrice';
import ReservationCodeForm from '@/wabunka-src/features/Experience/subFeatures/Reservation/pages/Reserve/components/ReservationSection/ReservationCodeForm/ReservationCodeForm.vue';
import CompactGuestDetailSection from '@/wabunka-src/features/Experience/subFeatures/Reservation/pages/Reserve/components/ReservationSection/ReservationStep2Form/CompactGuestDetailSection/CompactGuestDetailSection.vue';
import CompactOptionSection from '@/wabunka-src/features/Experience/subFeatures/Reservation/pages/Reserve/components/ReservationSection/ReservationStep2Form/CompactOptionSection/CompactOptionSection.vue';
import CreditCardFields from '@/wabunka-src/features/Experience/subFeatures/Reservation/pages/Reserve/components/ReservationSection/ReservationStep2Form/CreditCardFields/CreditCardFields.vue';
import HostDetailsSection from '@/wabunka-src/features/Experience/subFeatures/Reservation/pages/Reserve/components/ReservationSection/ReservationStep2Form/HostDetailsSection/HostDetailsSection.vue';
import ReservationFormSection from '@/wabunka-src/features/Experience/subFeatures/Reservation/pages/Reserve/components/ReservationSection/ReservationStep2Form/ReservationFormSection/ReservationFormSection.vue';

interface Props {
  onSuccess: () => void;
  onClickBackButton: () => void;
  experienceEvent: WabunkaExperienceEventViewModel;
  preReservationPrice: ExperiencePreReservationPriceViewModel;
  preReservationPriceWithGuestType?: ExperiencePreReservationPriceWithGuestTypeViewModel;
  paymentMethod: PaymentMethod;
  /** 新動線（アコーディオン分割）で描画するかどうか。AB の割当は呼び出し側が確定させてから渡す */
  isAccordion: boolean;
  /** 新動線の特典コード。内訳の欄と同じ状態を見るため呼び出し側が持つ */
  codeRedemption?: ReservationCodeRedemption;
}

const props = defineProps<Props>();

const {
  form,
  onClickStep2NextButton,
  paymentErrorMessages,
  getErrorMessage,
  applyCode,
  removeCode,
  errors,
  hasError,
  isSending,
  isPaymentSkip,
  reservationTypeChangedToFincode,
  hasPaymentError,
} = useGlobalWabunkaReservationForm();

// 入力中の欄に形式エラーを出すと、メールを 1 文字打った時点から赤くなる。一度離れた欄だけを対象にする。
// 欄ごとに blur を配るとセクションを跨いで prop を通すことになるため、まとめて拾って aria-label で引く
const blurredFieldNames = ref<string[]>([]);
// 予約質問の欄だけは aria-label（answer-<index>）がエラーのキー（reservationQuestionAnswers[<index>]）と違う
const toFieldName = (label: string) => {
  const questionIndex = /^answer-(\d+)$/.exec(label)?.[1];
  return questionIndex === undefined ? `step2.${label}` : `step2.reservationQuestionAnswers[${questionIndex}]`;
};
const onFocusOut = (event: FocusEvent) => {
  const label = (event.target as HTMLElement | null)?.getAttribute?.('aria-label');
  if (!label) return;
  const fieldName = toFieldName(label);
  if (!blurredFieldNames.value.includes(fieldName)) blurredFieldNames.value.push(fieldName);
};

// 新動線は送信へ到達できないので、Continue の活性と同じ core の判定から理由を引いて送信前に出す（旧動線は送信で理由が出るため変えない）
const unfulfilledFieldErrors = computed(() => FULFILLABLE_RESERVATION_SECTION_IDS.flatMap((sectionId) => listReservationSectionErrors(sectionId, form.value.step2)).filter((error) => !error.isBlank));

const getSectionErrorMessage = (fieldName: string) => {
  const submittedMessage = getErrorMessage(fieldName);
  if (submittedMessage || !props.isAccordion || !blurredFieldNames.value.includes(fieldName)) return submittedMessage;
  return getMessageByFieldName(unfulfilledFieldErrors.value, fieldName);
};

const isSectionFulfilled = (sectionId: FulfillableReservationSectionId) => isReservationSectionFulfilled(sectionId, form.value.step2);

const accordion = useReservationAccordion(toRef(props, 'experienceEvent'), isSectionFulfilled);
// 旧動線は全セクションが着地時から見えていて、到達が新動線と同じ意味にならないため送らない（D-023）
if (props.isAccordion) useReservationSectionGaEvents(() => props.experienceEvent.experience.id, accordion.state);

// payment は充足を判定しない（カード情報は PSP の iframe 内・利用規約同意は送信時の条件。D-012）
const sectionFulfillment = computed(() => ({
  contactInfo: isSectionFulfilled('contactInfo'),
  additionalOptions: isSectionFulfilled('additionalOptions'),
  hostDetails: isSectionFulfilled('hostDetails'),
  payment: false,
}));

// 代理予約では S1 の姓名・メールが予約者本人の欄になるため、折りたたみを開いている間は参加者を指す補足を出さない。会社名だけかどうかで出し分けると入力のたびに見出しが動く
const contactInfoSubtitle = computed(() => (isCompanyOrSomeoneElseFormOpen(form.value.step2) ? '' : "The lead participant's info"));

const hasReservationQuestions = computed(() => form.value.step2.reservationQuestionAnswers.length > 0);

// 規約同意チェックボックスの状態
const hasConsented = ref(false);
// usePaymentClient composableを使用して決済情報入力フォームをセットアップ。
// paymentMethod は予約種別で変わる（B-1）ため getter を渡し、フォーム読込時に最新の PSP を解決させる。
const { loadPaymentForm, unLoadPaymentForm } = usePaymentClient(() => props.paymentMethod);

// 価格APIレスポンス待ちかどうか
const isPriceLoading = computed(() => props.preReservationPriceWithGuestType === undefined);

// 0円等で決済自体をスキップするかどうか（ビジネスロジック）
const shouldSkipPayment = computed(() => {
  const price = props.preReservationPriceWithGuestType;
  if (price === undefined) return false;
  return isPaymentSkip(price.totalPriceIncludingTax);
});

// 決済フォームの表示・ロードが不要かどうか（価格未確定 or 決済スキップ）
const shouldHidePaymentForm = computed(() => isPriceLoading.value || shouldSkipPayment.value);

// PSP のフォームは display:none の器に差すと 0 幅で組み上がり、開いても崩れたままになる。
// 新動線では S4 が開いているときだけ読み込む。読み込み済みのフォームは畳んでも捨てない（作り直すと入力が消える。D-025）
const isPaymentSectionExpanded = computed(() => !props.isAccordion || accordion.isOpen('payment'));

// 畳まれたセクションの中で落ちたエラーは、そのセクションを開かないとゲストから見えない
const onClickSubmit = async () => {
  await onClickStep2NextButton(props.onSuccess, shouldSkipPayment.value);
  if (!props.isAccordion) return;
  const unfulfilledSectionId = FULFILLABLE_RESERVATION_SECTION_IDS.find((sectionId) => !isSectionFulfilled(sectionId));
  if (unfulfilledSectionId) accordion.openSection(unfulfilledSectionId);
};

const thumbnailImageUrl = computed(() => {
  const experienceImages = props.experienceEvent.experience.images;
  // 運用上体験画像が存在しない場合は起きないが、システム上は起こり得るのでチェック
  return experienceImages.length > 0 ? experienceImages[0].imageUrl : undefined;
});

const optionFieldsProps = computed(() => ({
  experienceEvent: props.experienceEvent,
  getErrorMessage: getSectionErrorMessage,
  onClickImage: props.onClickBackButton,
  slug: props.experienceEvent.experience.slug,
  thumbnailImageUrl: thumbnailImageUrl.value,
}));

// Error Messageの出し分け（Code入力はドロワーに置き換わる予定なので、時限的な実装。本来は ErrorMessage が存在するかの判別を行
// い、該当する場合は "Required fields are missing" message を表示するのみでOK）
const errorMessages = computed<string[]>(() => {
  const requiredMessage = 'Required fields are missing';
  const codeErrorMessage = getErrorMessage('step2.code.codeName');
  if (errors.value.length === 1 && codeErrorMessage) {
    return [codeErrorMessage];
  }

  if (errors.value.length > 1 && codeErrorMessage) {
    return [requiredMessage, codeErrorMessage];
  }

  return [requiredMessage];
});

const submittedCodeErrorMessage = computed(() => getErrorMessage('step2.code.codeName'));

// 弾かれたコードを外す・差し替えたら、そのエラーは役目を終える。旧動線は送り直すまで残る挙動を変えない（D-025）
watch(
  () => props.codeRedemption?.appliedCodeName,
  () => {
    if (!props.isAccordion) return;
    errors.value = errors.value.filter((error) => error.fieldName !== 'step2.code.codeName');
  },
);

// リトライ用のヘルパー関数をcomposablesまたはutilsに移動することを検討
const RETRY_DELAY_MS = 1000;
const MAX_RETRIES = 3;

// PaymentFormの読み込み状態を管理
const isPaymentFormLoaded = ref(false);
const isPaymentFormLoading = ref(false);
// カード入力 DOM を作り直すための key。PSP のフォームは iframe なので、値を消すには DOM ごと捨てる必要がある
const paymentFormInstanceKey = ref(0);
// 読み込みの途中で畳まれたか。描画には使わないので ref にしない
let wasPaymentSectionCollapsedWhileLoading = false;

// PaymentFormを読み込む関数
const loadPaymentFormWithRetry = async () => {
  if (isPaymentFormLoading.value || isPaymentFormLoaded.value) {
    return;
  }

  isPaymentFormLoading.value = true;
  wasPaymentSectionCollapsedWhileLoading = false;
  let retries = 0;
  const maxRetries = MAX_RETRIES;

  while (retries < maxRetries) {
    try {
      await loadPaymentForm();
      isPaymentFormLoaded.value = true;
      break;
    } catch (error) {
      retries++;
      if (retries >= maxRetries) {
        // 最終的に失敗した場合の追加エラー（合計4回試行）
        const finalErr = new Error(`Payment form could not be loaded after 4 attempts: ${(error as Error).message}`);
        captureException(finalErr);
        break;
      }
      await sleep(RETRY_DELAY_MS);
      // sleep 中に決済フォーム不要状態へ変化した場合（0円確定・コード適用等）はリトライ中断
      if (shouldHidePaymentForm.value) break;
    }
  }

  isPaymentFormLoading.value = false;

  // 読み込んでいる最中に一度でも畳まれると、PSP のフォームは display:none の器へ 0 幅で組み上がり、
  // 開き直しても崩れたまま残る。そのときだけ捨てて作り直す（開いたまま読み終えたフォームは畳んでも捨てない。D-025）
  if (isPaymentFormLoaded.value && wasPaymentSectionCollapsedWhileLoading) {
    unLoadPaymentForm();
    isPaymentFormLoaded.value = false;
    paymentFormInstanceKey.value++;
    // 畳んだまま戻ってこなければ、次に開いたときの watch が読み直す
    if (!isPaymentSectionExpanded.value) return;
    await nextTick();
    await loadPaymentFormWithRetry();
  }
};

onMounted(async () => {
  await nextTick();
  if (!shouldHidePaymentForm.value && isPaymentSectionExpanded.value) {
    await loadPaymentFormWithRetry();
  }
});

// 畳まれている間に必要になった読み込みを、開いた時点で始める（旧動線では常に true なので発火しない）
watch(isPaymentSectionExpanded, async (expanded) => {
  if (!expanded) {
    if (isPaymentFormLoading.value) wasPaymentSectionCollapsedWhileLoading = true;
    return;
  }
  if (isPaymentFormLoading.value || isPaymentFormLoaded.value) return;
  if (shouldHidePaymentForm.value) return;
  await nextTick();
  await loadPaymentFormWithRetry();
});

// shouldHidePaymentFormの変化を監視してPaymentFormの読み込みを制御
watch(shouldHidePaymentForm, async (newVal, oldVal) => {
  // フォームロード中は変化を無視（競合防止）
  if (isPaymentFormLoading.value) return;

  // フォーム不要 → 表示が必要になった場合（価格確定で有料と判明、コード解除等）
  if (oldVal && !newVal) {
    unLoadPaymentForm();
    isPaymentFormLoaded.value = false;
    if (!isPaymentSectionExpanded.value) return;
    await nextTick();
    await loadPaymentFormWithRetry();
    return;
  }
  // 初回読み込み等: 表示が必要かつ未読み込みの場合
  if (props.experienceEvent.id && !newVal && !isPaymentFormLoaded.value && isPaymentSectionExpanded.value) {
    await loadPaymentFormWithRetry();
  }
});

// 決済プロバイダ（Pay.jp / Fincode）が切り替わった場合にフォームを読み込み直す。
// 予約確定時に種別が Request へ変わり Fincode へ自動切替したケース（B-1 のレース）で発生する。
// Pay.jp 側の入力 DOM は CreditCardFields の v-if 切替で破棄されるため、新プロバイダのフォームを読み込めばよい。
watch(
  () => props.paymentMethod,
  async (newMethod, oldMethod) => {
    if (newMethod === oldMethod) return;
    if (isPaymentFormLoading.value) return;
    if (shouldHidePaymentForm.value) return;
    isPaymentFormLoaded.value = false;
    if (!isPaymentSectionExpanded.value) return;
    // 新しいプロバイダ用のフォーム DOM が描画されるのを待ってから読み込む
    await nextTick();
    await loadPaymentFormWithRetry();
  },
);

// 決済エラー後は、カード入力 DOM ごと作り直して PSP のフォームを読み込み直す。
// 前のカード値が残っていると、ゲストが同じカードで再送してしまい同じ決済エラーを繰り返す。
watch(hasPaymentError, async (occurred) => {
  if (!occurred) return;
  if (isPaymentFormLoading.value) return;
  if (shouldHidePaymentForm.value) return;
  unLoadPaymentForm();
  isPaymentFormLoaded.value = false;
  paymentFormInstanceKey.value++;
  if (!isPaymentSectionExpanded.value) return;
  await nextTick();
  await loadPaymentFormWithRetry();
});

onUnmounted(() => {
  if (isPaymentFormLoaded.value) {
    unLoadPaymentForm();
    isPaymentFormLoaded.value = false;
  }
});
</script>
